import { Trash } from '@medusajs/icons';
import { HttpTypes } from '@medusajs/types';
import { clx, Table, Text, toast, usePrompt } from '@medusajs/ui';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useDeleteLineItem, useUpdateLineItem } from '@lib/query/mutations/cart';
import { getPricesForVariant } from '@lib/util/get-product-price';
import { isCashOnlyProductVariant } from '@lib/util/is-cash-only-product-variant';
import { convertToLocale } from '@lib/util/money';
import { stripSkuSuffix } from '@lib/util/strip-sku-suffix';
import Counter from '@modules/common/components/counter';
import LineItemUnitPrice from '@modules/common/components/line-item-unit-price';
import Tag from '@modules/common/components/tag';
import Spinner from '@modules/common/icons/spinner';
import Thumbnail from '@modules/products/components/thumbnail';

interface ItemProps {
  item: HttpTypes.StoreCartLineItem;
  type?: 'full' | 'preview';
  step?: string | null;
  idx: number;
  showImg?: boolean;
  showIdx?: boolean;
}

const Item = ({
  item,
  type = 'full',
  step,
  idx,
  showImg = true,
  showIdx = false,
}: ItemProps) => {
  const maxQtyFromInventory = item.variant?.inventory_quantity ?? 0;

  const [updating, setUpdating] = useState(false);
  const [{ dirty, quantity }, setState] = useState<{
    dirty: boolean;
    quantity: number;
  }>({
    dirty: false,
    quantity: item.quantity,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const { handle } = item.variant?.product ?? {};
  const { calculated_price_number } = getPricesForVariant(item.variant) || {};
  const deletePrompt = usePrompt();
  const { mutateAsync: updateLineItem } = useUpdateLineItem({
    onError: (error, variables) => {
      Sentry.captureException(error, {
        tags: { action: 'update_cart_item_quantity' },
        extra: { lineItemId: variables.lineItemId, quantity: variables.quantity },
      });
      toast.error('更新失敗', {
        description: '更新數量失敗，請稍後再試',
        duration: 4000,
      });
    },
  });
  const deleteLineItemMutation = useDeleteLineItem();

  // Show out-of-stock indicator only on checkout step 1 (cart review).
  const showOutOfStock = maxQtyFromInventory === 0 && step === '1';

  const subTotal = useMemo(() => {
    return item.variant?.calculated_price?.currency_code
      ? convertToLocale({
          amount: (calculated_price_number ?? 0) * quantity,
          currency_code: item.variant?.calculated_price?.currency_code,
        })
      : '-';
  }, [
    calculated_price_number,
    item.variant?.calculated_price?.currency_code,
    quantity,
  ]);

  const changeQuantity = useCallback(
    async (qty: number) => {
      setUpdating(true);
      const inventoryQty = item.variant?.inventory_quantity ?? null;
      try {
        await updateLineItem({
          lineItemId: item.id,
          quantity: inventoryQty !== null ? Math.min(qty, inventoryQty) : qty,
        });
      } finally {
        setUpdating(false);
      }
    },
    [item.id, item.variant?.inventory_quantity, updateLineItem]
  );

  const handleCounterChange = useCallback((value: number) => {
    setState({
      dirty: true,
      quantity: value,
    });
  }, []);

  const deleteEntity = async (id: string) => {
    const userHasConfirmed = await deletePrompt({
      title: '確認刪除？',
      description: '您確定要將此商品從購物車中刪除嗎？',
      cancelText: '取消',
      confirmText: '確認',
    });
    if (userHasConfirmed) {
      setIsDeleting(true);
      try {
        await deleteLineItemMutation.mutateAsync(id);
        toast.success('刪除成功', {
          description: '已將此商品從購物車中移除',
          duration: 4000,
        });
      } catch (error) {
        Sentry.captureException(error, {
          tags: { action: 'delete_cart_item' },
          extra: { itemId: id },
        });
        toast.error('刪除失敗', {
          description: '刪除商品時發生錯誤',
          duration: 4000,
        });
      } finally {
        setIsDeleting(false);
      }
    }
  };

  useEffect(() => {
    if (!dirty) {
      return;
    }
    changeQuantity(quantity);
  }, [changeQuantity, quantity, dirty]);

  useEffect(() => {
    setState((state) => ({ ...state, quantity: item.quantity as number }));
  }, [item.quantity]);

  const isCashOnly = isCashOnlyProductVariant(item.variant);

  return (
    <Table.Row className="w-full" data-testid="product-row">
      {showIdx && (
        <Table.Cell>
          <Text className="text-grey-60">{idx + 1}.</Text>
        </Table.Cell>
      )}
      {showImg && (
        <Table.Cell className="w-24 p-4">
          <Link
            to={`/products/${handle}`}
            className={clx('relative flex', {
              'w-16': type === 'preview',
              'small:w-24 w-12': type === 'full',
            })}
          >
            <Thumbnail
              thumbnail={item.variant?.product?.thumbnail}
              images={item.variant?.product?.images}
              size="square"
            />
            {showOutOfStock && (
              <div className="bg-white-10 absolute aspect-square w-[90px] rounded-md opacity-70"></div>
            )}
          </Link>
        </Table.Cell>
      )}

      <Table.Cell className="text-left">
        <Link to={`/products/${handle}`} className="flex">
          <div
            className="text-black-30 line-clamp-2 w-full hover:underline"
            data-testid="product-title"
          >
            {item.product_title}
          </div>
        </Link>
        <div className="text-grey-30">國際條碼 {stripSkuSuffix(item.variant?.ean)}</div>
        <div className="text-grey-30">
          庫存數量 {item.variant?.inventory_quantity ?? 0}
        </div>
      </Table.Cell>

      {type === 'full' && (
        <Table.Cell className="py-4">
          <LineItemUnitPrice item={item} style="tight" isCashOnly={isCashOnly} />
        </Table.Cell>
      )}
      {type === 'full' && step === '1' && (
        <Table.Cell className="pr-0">
          <div className="flex flex-col items-center justify-center">
            {!showOutOfStock && (
              <Tag className="bg-grey-50 text-white-10" text="現貨" />
            )}
          </div>
        </Table.Cell>
      )}

      {type === 'full' && step === '1' && (
        <Table.Cell>
          <div className="flex items-center justify-end gap-4">
            {showOutOfStock ? (
              <div className="text-body-bold text-black-30">售完補貨中</div>
            ) : (
              <Counter
                count={quantity}
                onChange={handleCounterChange}
                disabled={updating}
                delay={500}
                max={maxQtyFromInventory}
                className="max-w-[180px] flex-auto"
                data-testid={`cart-item-counter-${item.id}`}
                errMessage={
                  quantity > (item.variant?.inventory_quantity ?? 0)
                    ? '庫存不足 請調整購買數量'
                    : ''
                }
              />
            )}

            {isDeleting ? (
              <Spinner className="animate-spin" />
            ) : (
              <Trash
                onClick={() => deleteEntity(item.id)}
                className="primary-icon"
                data-testid={`cart-item-delete-${item.id}`}
              />
            )}
          </div>
        </Table.Cell>
      )}
      {step !== '1' && (
        <Table.Cell className="text-center">{quantity}</Table.Cell>
      )}
      <Table.Cell>
        {!showOutOfStock && (
          <div className="text-body-bold text-black-30 text-right">
            {subTotal}
          </div>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

export default Item;
